import { useCallback, useEffect, useState } from "react";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Crown,
  Loader2,
  Trophy,
  X,
} from "lucide-react";
import type {
  LeaderboardEntry,
  LeaderboardResponse,
  RepDetailResponse,
  RepRole,
} from "@/lib/types";
import { cn, formatCompactCurrency } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Date range model
// ---------------------------------------------------------------------------

type Preset = "week" | "month" | "quarter" | "ytd" | "custom";

type Range = {
  preset: Preset;
  /** Anchor date the period is computed against. Custom ranges ignore this. */
  anchor: Date;
  /** Inclusive start. */
  start: Date;
  /** Exclusive end. */
  end: Date;
  label: string;
};

const AVATAR_FALLBACK_BG = "#7787f7";

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function startOfWeek(d: Date): Date {
  // ISO-ish week starting Monday.
  const x = startOfDay(d);
  const day = x.getDay(); // 0 = Sun, 1 = Mon, ...
  const offset = (day + 6) % 7; // days since last Monday
  x.setDate(x.getDate() - offset);
  return x;
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function startOfQuarter(d: Date): Date {
  const q = Math.floor(d.getMonth() / 3);
  return new Date(d.getFullYear(), q * 3, 1);
}

function startOfYear(d: Date): Date {
  return new Date(d.getFullYear(), 0, 1);
}

function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

function addQuarters(d: Date, n: number): Date {
  return addMonths(startOfQuarter(d), n * 3);
}

function addYears(d: Date, n: number): Date {
  return new Date(d.getFullYear() + n, 0, 1);
}

function formatMonth(d: Date): string {
  return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

function formatQuarter(d: Date): string {
  const q = Math.floor(d.getMonth() / 3) + 1;
  return `Q${q} ${d.getFullYear()}`;
}

function formatWeek(start: Date): string {
  const end = addDays(start, 6);
  const sameMonth = start.getMonth() === end.getMonth();
  const startStr = start.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  const endStr = end.toLocaleDateString("en-US", {
    month: sameMonth ? undefined : "short",
    day: "numeric",
    year: "numeric",
  });
  return `${startStr} – ${endStr}`;
}

function formatDateInput(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function buildRange(preset: Preset, anchor: Date): Range {
  switch (preset) {
    case "week": {
      const start = startOfWeek(anchor);
      const end = addDays(start, 7);
      return { preset, anchor, start, end, label: formatWeek(start) };
    }
    case "month": {
      const start = startOfMonth(anchor);
      const end = addMonths(start, 1);
      return { preset, anchor, start, end, label: formatMonth(start) };
    }
    case "quarter": {
      const start = startOfQuarter(anchor);
      const end = addQuarters(start, 1);
      return { preset, anchor, start, end, label: formatQuarter(start) };
    }
    case "ytd": {
      const start = startOfYear(anchor);
      const end = addYears(start, 1);
      // Year-to-date label: "YTD 2026"
      return {
        preset,
        anchor,
        start,
        end,
        label: `YTD ${start.getFullYear()}`,
      };
    }
    case "custom": {
      // For custom, anchor is the start, and we'll set end externally.
      const start = startOfDay(anchor);
      const end = addDays(start, 1);
      return {
        preset,
        anchor,
        start,
        end,
        label: `${formatDateInput(start)} – ${formatDateInput(end)}`,
      };
    }
  }
}

function shiftRange(range: Range, direction: -1 | 1): Range {
  if (range.preset === "custom") return range;
  switch (range.preset) {
    case "week":
      return buildRange("week", addDays(range.start, direction * 7));
    case "month":
      return buildRange("month", addMonths(range.start, direction));
    case "quarter":
      return buildRange("quarter", addQuarters(range.start, direction));
    case "ytd":
      return buildRange("ytd", addYears(range.start, direction));
  }
}

function isFutureRange(range: Range): boolean {
  return range.start.getTime() > Date.now();
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function LeaderboardPage() {
  const [range, setRange] = useState<Range>(() => buildRange("month", new Date()));
  const [customStart, setCustomStart] = useState(() =>
    formatDateInput(startOfMonth(new Date()))
  );
  const [customEnd, setCustomEnd] = useState(() =>
    formatDateInput(addMonths(startOfMonth(new Date()), 1))
  );
  const [role, setRole] = useState<RepRole>("csm");
  const [data, setData] = useState<LeaderboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<LeaderboardEntry | null>(null);

  const load = useCallback(
    async (r: Range, ro: RepRole) => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({
          start: r.start.toISOString(),
          end: r.end.toISOString(),
          role: ro,
        });
        const res = await fetch(`/api/leaderboard?${params.toString()}`, {
          credentials: "include",
        });
        const body = await res.json();
        if (!res.ok) throw new Error(body.error ?? "Failed to load");
        setData(body as LeaderboardResponse);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    void load(range, role);
  }, [range, role, load]);

  const refresh = useCallback(() => {
    void load(range, role);
  }, [load, range, role]);

  const entries = data?.entries ?? [];
  const leader = entries[0];
  const rest = entries.slice(1);

  return (
    <div className="mx-auto max-w-4xl p-8 pb-20">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <Trophy className="h-6 w-6 text-primary" />
            Leaderboard
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Agencies sold per rep, derived from activations.
          </p>
        </div>
        <RoleToggle value={role} onChange={setRole} />
      </div>

      <DateControls
        range={range}
        onChange={setRange}
        customStart={customStart}
        customEnd={customEnd}
        onCustomStart={setCustomStart}
        onCustomEnd={setCustomEnd}
      />

      {error && (
        <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      {loading && !data ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : entries.length === 0 ? (
        <div className="rounded-xl border bg-card p-12 text-center text-sm text-muted-foreground">
          No activations for {role === "ae" ? "AEs" : "CSMs"} in this range.
        </div>
      ) : (
        <>
          {leader && (
            <LeaderCard
              entry={leader}
              onClick={() => setSelected(leader)}
            />
          )}
          {rest.length > 0 && (
            <div className="mt-6 overflow-hidden rounded-xl border bg-card">
              {rest.map((entry, i) => (
                <LeaderboardRow
                  key={`${entry.name}-${i}`}
                  entry={entry}
                  rank={i + 2}
                  onClick={() => setSelected(entry)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {selected && (
        <AboutCard
          entry={selected}
          onClose={() => setSelected(null)}
          onUpdated={refresh}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Role toggle
// ---------------------------------------------------------------------------

function RoleToggle({
  value,
  onChange,
}: {
  value: RepRole;
  onChange: (v: RepRole) => void;
}) {
  const opts: { id: RepRole; label: string }[] = [
    { id: "csm", label: "CSMs" },
    { id: "ae", label: "AEs" },
  ];
  return (
    <div className="inline-flex overflow-hidden rounded-md border bg-white text-xs">
      {opts.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange(o.id)}
          className={cn(
            "px-3 py-1.5 font-medium transition-colors",
            value === o.id
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:bg-slate-50"
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Date controls (preset buttons + caret nav + secondary custom picker)
// ---------------------------------------------------------------------------

function DateControls({
  range,
  onChange,
  customStart,
  customEnd,
  onCustomStart,
  onCustomEnd,
}: {
  range: Range;
  onChange: (r: Range) => void;
  customStart: string;
  customEnd: string;
  onCustomStart: (s: string) => void;
  onCustomEnd: (s: string) => void;
}) {
  const presets: { id: Preset; label: string }[] = [
    { id: "week", label: "This Week" },
    { id: "month", label: "This Month" },
    { id: "quarter", label: "This Quarter" },
    { id: "ytd", label: "Year to Date" },
  ];

  const canGoForward = !isFutureRange(shiftRange(range, 1));

  function applyCustom(start: string, end: string): void {
    const s = new Date(start);
    const e = new Date(end);
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return;
    if (s.getTime() >= e.getTime()) return;
    onChange({
      preset: "custom",
      anchor: s,
      start: startOfDay(s),
      end: startOfDay(e),
      label: `${formatDateInput(s)} – ${formatDateInput(e)}`,
    });
  }

  return (
    <div className="mb-6 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex overflow-hidden rounded-md border bg-white text-xs">
          {presets.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => onChange(buildRange(p.id, new Date()))}
              className={cn(
                "px-3 py-1.5 font-medium transition-colors",
                range.preset === p.id
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-slate-50"
              )}
            >
              {p.label}
            </button>
          ))}
        </div>

        {range.preset !== "custom" && (
          <div className="ml-2 flex items-center gap-1.5 rounded-md border bg-white px-2 py-1">
            <button
              type="button"
              onClick={() => onChange(shiftRange(range, -1))}
              className="rounded p-1 text-muted-foreground hover:bg-slate-100 hover:text-foreground"
              title="Previous period"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="min-w-[140px] text-center text-sm font-medium tabular-nums">
              {range.label}
            </span>
            <button
              type="button"
              onClick={() => onChange(shiftRange(range, 1))}
              disabled={!canGoForward}
              className="rounded p-1 text-muted-foreground hover:bg-slate-100 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent"
              title="Next period"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
        <span>Custom range:</span>
        <input
          type="date"
          value={customStart}
          onChange={(e) => {
            onCustomStart(e.target.value);
            applyCustom(e.target.value, customEnd);
          }}
          className="rounded border bg-white px-1.5 py-1 text-xs outline-none focus:ring-1 focus:ring-ring"
        />
        <span>to</span>
        <input
          type="date"
          value={customEnd}
          onChange={(e) => {
            onCustomEnd(e.target.value);
            applyCustom(customStart, e.target.value);
          }}
          className="rounded border bg-white px-1.5 py-1 text-xs outline-none focus:ring-1 focus:ring-ring"
        />
        {range.preset === "custom" && (
          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
            Custom range active
          </span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Avatar (with initials fallback on #7787f7)
// ---------------------------------------------------------------------------

function getInitials(name: string): string {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0]?.charAt(0) ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1]!.charAt(0) : "";
  return `${first}${last}`.toUpperCase() || "?";
}

function Avatar({
  name,
  url,
  size,
  className,
}: {
  name: string;
  url: string | null;
  size: number;
  className?: string;
}) {
  const [errored, setErrored] = useState(false);
  const dim = `${size}px`;
  const fontSize = Math.max(10, Math.round(size * 0.38));
  if (url && !errored) {
    return (
      <img
        src={url}
        alt={name}
        onError={() => setErrored(true)}
        className={cn("rounded-full object-cover", className)}
        style={{ width: dim, height: dim }}
      />
    );
  }
  return (
    <div
      className={cn(
        "flex items-center justify-center rounded-full font-semibold text-white",
        className
      )}
      style={{
        width: dim,
        height: dim,
        backgroundColor: AVATAR_FALLBACK_BG,
        fontSize,
      }}
    >
      {getInitials(name)}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Leader (rank #1) card
// ---------------------------------------------------------------------------

function LeaderCard({
  entry,
  onClick,
}: {
  entry: LeaderboardEntry;
  onClick: () => void;
}) {
  const AVATAR_SIZE = 120;
  return (
    <div
      className="relative rounded-2xl border bg-gradient-to-b from-amber-50 to-card px-8 pb-8 text-center shadow-sm"
      style={{ marginTop: AVATAR_SIZE / 2, paddingTop: AVATAR_SIZE / 2 + 24 }}
    >
      <div
        className="absolute left-1/2 -translate-x-1/2"
        style={{ top: -(AVATAR_SIZE / 2) }}
      >
        <div className="relative">
          <Crown
            className="absolute left-1/2 top-0 h-9 w-9 -translate-x-1/2 -translate-y-3/4 text-amber-500 drop-shadow-sm"
            fill="currentColor"
          />
          <Avatar
            name={entry.name}
            url={entry.avatar_url}
            size={AVATAR_SIZE}
            className="ring-4 ring-amber-300"
          />
        </div>
      </div>

      <button
        type="button"
        onClick={onClick}
        className="text-lg font-semibold tracking-tight hover:underline"
      >
        {entry.name}
      </button>
      <div className="mt-2 text-3xl font-bold tabular-nums">
        {entry.activation_count}
      </div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">
        {entry.activation_count === 1 ? "agency" : "agencies"} activated
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ranked row (Strava KOM style)
// ---------------------------------------------------------------------------

function LeaderboardRow({
  entry,
  rank,
  onClick,
}: {
  entry: LeaderboardEntry;
  rank: number;
  onClick: () => void;
}) {
  return (
    <div className="flex items-center gap-4 border-b px-5 py-3 last:border-b-0 hover:bg-slate-50/60">
      <span className="w-8 text-sm font-semibold tabular-nums text-muted-foreground">
        {rank}
      </span>
      <Avatar name={entry.name} url={entry.avatar_url} size={40} />
      <button
        type="button"
        onClick={onClick}
        className="flex-1 text-left text-sm font-medium hover:underline"
      >
        {entry.name}
      </button>
      <span className="text-base font-semibold tabular-nums">
        {entry.activation_count}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// About card modal
// ---------------------------------------------------------------------------

function AboutCard({
  entry,
  onClose,
  onUpdated,
}: {
  entry: LeaderboardEntry;
  onClose: () => void;
  onUpdated: () => void;
}) {
  const [detail, setDetail] = useState<RepDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(entry.avatar_url);
  const [editing, setEditing] = useState(false);
  const [draftUrl, setDraftUrl] = useState(entry.avatar_url ?? "");

  const repId = detail?.rep?.id ?? entry.rep_id;

  const loadDetail = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/leaderboard/reps/${encodeURIComponent(entry.name)}`,
        { credentials: "include" }
      );
      const body = await res.json();
      if (!res.ok) throw new Error(body.error ?? "Failed to load rep");
      setDetail(body as RepDetailResponse);
      if (body.rep?.avatar_url) {
        setAvatarUrl(body.rep.avatar_url);
        setDraftUrl(body.rep.avatar_url);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load rep");
    } finally {
      setLoading(false);
    }
  }, [entry.name]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  async function ensureRep(): Promise<string | null> {
    if (repId) return repId;
    try {
      const res = await fetch("/api/leaderboard/reps", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: entry.name, role: entry.role }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error ?? "Failed to create rep");
      const id = body.rep?.id as string | undefined;
      if (!id) return null;
      setDetail((d) =>
        d
          ? { ...d, rep: body.rep }
          : {
              rep: body.rep,
              totals: { total_activations: 0, total_mrr_cents: 0 },
            }
      );
      return id;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create rep");
      return null;
    }
  }

  async function saveAvatarUrl(): Promise<void> {
    setError(null);
    setSaving(true);
    try {
      const id = await ensureRep();
      if (!id) {
        setSaving(false);
        return;
      }
      const trimmed = draftUrl.trim();
      const res = await fetch(`/api/leaderboard/reps/${id}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ avatar_url: trimmed.length > 0 ? trimmed : "" }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error ?? "Save failed");
      setAvatarUrl(body.rep?.avatar_url ?? null);
      setDetail((d) => (d ? { ...d, rep: body.rep } : d));
      setEditing(false);
      onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  const role = detail?.rep?.role ?? entry.role;
  const totals = detail?.totals;

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl border bg-card shadow-xl"
      >
        <div className="flex items-start justify-between border-b px-6 py-4">
          <h3 className="text-sm font-semibold">About</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-slate-100"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex flex-col items-center px-6 py-6">
          <Avatar name={entry.name} url={avatarUrl} size={96} />
          <div className="mt-4 text-lg font-semibold tracking-tight">
            {entry.name}
          </div>
          <div className="mt-1 text-xs uppercase tracking-wide text-muted-foreground">
            {role === "ae" ? "Account Executive" : "Customer Success Manager"}
          </div>

          {editing ? (
            <div className="mt-4 flex w-full max-w-sm items-center gap-2">
              <input
                type="url"
                placeholder="https://example.com/avatar.png"
                value={draftUrl}
                onChange={(e) => setDraftUrl(e.target.value)}
                className="flex-1 rounded-md border bg-white px-2 py-1.5 text-xs outline-none focus:ring-2 focus:ring-ring"
                autoFocus
              />
              <button
                type="button"
                onClick={() => void saveAvatarUrl()}
                disabled={saving}
                className="inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {saving ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Check className="h-3.5 w-3.5" />
                )}
                Save
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setDraftUrl(avatarUrl ?? "");
                  setError(null);
                }}
                className="rounded-md border bg-white px-2.5 py-1.5 text-xs font-medium hover:bg-slate-50"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="mt-4 inline-flex items-center gap-1.5 rounded-md border bg-white px-3 py-1.5 text-xs font-medium hover:bg-slate-50"
            >
              {avatarUrl ? "Edit photo URL" : "Set photo URL"}
            </button>
          )}

          {error && (
            <div className="mt-3 w-full rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
              {error}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 border-t px-6 py-5">
          <Stat
            label="Total agencies"
            value={
              loading
                ? "…"
                : (totals?.total_activations ?? 0).toLocaleString()
            }
          />
          <Stat
            label="CloudStudio MRR"
            value={
              loading
                ? "…"
                : formatCompactCurrency(totals?.total_mrr_cents ?? 0)
            }
          />
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border bg-slate-50/40 p-3 text-center">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}
